/**
 * LobbyReception
 *
 * Visual reception area for the Headquarters lobby floor.
 * Renders a stylized lobby scene with company branding on the wall,
 * digital displays showing public company info, a reception desk,
 * and a seating area. Public-facing information only -- internal
 * data lives on the upper floors.
 */

import React, { useState, useEffect } from 'react';
import { Monitor, Package, Users, TrendingUp, Star, Building2, UserPlus, Palette, ArrowUpRight } from 'lucide-react';
import type { CompanyState } from '@/types/company';
import type { Product, Employee } from '@/types/tycoon';
import type { ActiveEvent } from '@/types/dynamicEvents';
import type { GameDate } from '@/types/events';
import { NewspaperProp, NewspaperOverlay } from './newspaper';

interface LobbyReceptionProps {
  companyState: CompanyState;
  companyName: string;
  products: Product[];
  employees: Employee[];
  eventHistory?: ActiveEvent[];
  gameDate?: GameDate;
}

/** A single digital display mounted on the lobby wall. */
function WallDisplay({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`w-44 h-32 rounded-sm overflow-hidden flex flex-col
                  bg-gray-900 border border-gray-700 shadow-lg
                  relative ${className}`}
    >
      {/* Screen bezel */}
      <div className="absolute inset-0 rounded-sm ring-1 ring-inset ring-white/5" />
      {/* Screen content */}
      <div className="flex-1 flex flex-col items-center justify-center p-3 relative z-10">
        {children}
      </div>
      {/* Stand/mount */}
      <div className="h-1 bg-gray-700" />
    </div>
  );
}

/** Animated slide content for a display. */
function DisplaySlide({
  icon: Icon,
  value,
  label,
  color,
}: {
  icon: React.FC<{ className?: string }>;
  value: string;
  label: string;
  color: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5 animate-fadeInDisplay">
      <Icon className={`w-5 h-5 ${color}`} />
      <span className="text-xl font-bold text-white font-display tracking-wide">
        {value}
      </span>
      <span className="text-[10px] uppercase tracking-[0.15em] text-white/40">
        {label}
      </span>
    </div>
  );
}

/** Onboarding slide for the right display when all metrics are zero. */
function OnboardingSlide({
  icon: Icon,
  prompt,
  destination,
  color,
}: {
  icon: React.FC<{ className?: string }>;
  prompt: string;
  destination: string;
  color: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 animate-fadeInDisplay">
      <Icon className={`w-5 h-5 ${color}`} />
      <span className="text-[10px] text-center leading-relaxed text-white/70 px-1">
        {prompt}
      </span>
      <span className="flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] text-theme-accent">
        <ArrowUpRight className="w-3 h-3" />
        {destination}
      </span>
    </div>
  );
}

/** A simple chair shape. */
function Chair({ flip = false }: { flip?: boolean }) {
  return (
    <div className={`flex flex-col items-center ${flip ? 'scale-x-[-1]' : ''}`}>
      {/* Backrest */}
      <div className="w-8 h-5 rounded-t-md bg-theme-accent/15 border border-theme-border" />
      {/* Seat */}
      <div className="w-10 h-3 rounded-b-sm bg-theme-accent/10 border-x border-b border-theme-border" />
      {/* Legs */}
      <div className="flex justify-between w-9 mt-0.5">
        <div className="w-1 h-2 bg-theme-text-muted/20 rounded-b" />
        <div className="w-1 h-2 bg-theme-text-muted/20 rounded-b" />
      </div>
    </div>
  );
}

/** A potted plant decoration. */
function PottedPlant() {
  return (
    <div className="flex flex-col items-center">
      {/* Foliage */}
      <div className="relative">
        <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30" />
        <div className="absolute top-1 left-2 w-7 h-7 rounded-full bg-emerald-500/15" />
      </div>
      {/* Stem */}
      <div className="w-1 h-3 bg-emerald-600/30" />
      {/* Pot */}
      <div className="w-6 h-4 rounded-b-md bg-theme-text-muted/15 border border-theme-border" />
    </div>
  );
}

export const LobbyReception: React.FC<LobbyReceptionProps> = ({
  companyState,
  companyName,
  products,
  employees,
  eventHistory,
  gameDate,
}) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [isNewspaperOpen, setIsNewspaperOpen] = useState(false);

  const hasNews = (eventHistory ?? []).length > 0;

  const activeProducts = (products ?? []).filter(p => p.status === 'production').length;
  const totalProducts = (products ?? []).length;
  const employeeCount = (employees ?? []).length;
  const reputation = companyState?.reputation ?? 0;
  const marketShare = companyState?.marketShare ?? 0;

  // Detect empty state (new game -- no meaningful activity yet)
  const isEmptyState = employeeCount === 0 && totalProducts === 0 && reputation === 0;

  const onboardingSlides = [
    { icon: UserPlus, prompt: 'Hire your first employee', destination: 'HR -- Floor 2', color: 'text-blue-400' },
    { icon: Palette, prompt: 'Design your first phone', destination: 'Design Studio', color: 'text-cyan-400' },
    { icon: TrendingUp, prompt: 'Plan your budget', destination: 'Finance -- Floor 3', color: 'text-amber-400' },
  ];

  const highlightSlides = [
    { icon: Package, value: String(activeProducts), label: 'Products on Market', color: 'text-cyan-400' },
    { icon: Users, value: String(employeeCount), label: 'Team Members', color: 'text-blue-400' },
    { icon: TrendingUp, value: `${(marketShare * 100).toFixed(1)}%`, label: 'Market Share', color: 'text-amber-400' },
  ];

  const slideCount = isEmptyState ? onboardingSlides.length : highlightSlides.length;

  // Cycle the right display through slides
  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex(prev => (prev + 1) % slideCount);
    }, isEmptyState ? 5000 : 4000);
    return () => clearInterval(interval);
  }, [slideCount, isEmptyState]);

  const foundedYear = companyState?.year ?? 2000;

  return (
    <div className="h-full w-full flex flex-col overflow-hidden relative">

      {/* ── Wall Section ─────────────────────────────────────── */}
      <div
        className="flex-[5] relative flex flex-col items-center justify-center gap-6 px-8
                   bg-gradient-to-b from-theme-bg-tertiary via-theme-bg-secondary to-theme-bg-content/50"
      >
        {/* Subtle wall texture lines */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, currentColor 0px, currentColor 1px, transparent 1px, transparent 40px)',
          }}
        />

        {/* Company name plaque */}
        <div className="relative z-10 text-center">
          <div className="px-10 md:px-16 py-5 md:py-7 border-2 border-theme-accent/25 rounded-sm
                          bg-theme-bg-content/60 backdrop-blur-sm shadow-md">
            <h1 className="text-2xl md:text-4xl font-display tracking-[0.25em] uppercase text-theme-text-primary">
              {companyName || 'Company'}
            </h1>
            <div className="mt-2 flex items-center justify-center gap-3 text-xs text-theme-text-muted tracking-wider">
              <span>Est. {foundedYear}</span>
              <span className="w-1 h-1 rounded-full bg-theme-accent/50" />
              <span>Consumer Electronics</span>
            </div>
          </div>
        </div>

        {/* Wall-mounted digital displays */}
        <div className="relative z-10 flex gap-8 md:gap-16">
          {/* Left display: Company profile or getting-started guide */}
          <WallDisplay>
            {isEmptyState ? (
              <div className="w-full space-y-2 text-[11px]">
                <div className="flex items-center gap-1.5 mb-2">
                  <Monitor className="w-3 h-3 text-theme-accent" />
                  <span className="text-[9px] uppercase tracking-[0.15em] text-white/40">
                    Getting Started
                  </span>
                </div>
                {['Hire a team', 'Design a product', 'Launch to market'].map((step, i) => (
                  <div key={step} className="flex items-center gap-1.5">
                    <div className={`w-1 h-1 rounded-full flex-shrink-0 transition-colors duration-300 ${
                      i === slideIndex ? 'bg-theme-accent' : 'bg-white/20'
                    }`} />
                    <span className={`text-[10px] transition-colors duration-300 ${
                      i === slideIndex ? 'text-white/80' : 'text-white/40'
                    }`}>{step}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full space-y-2 text-[11px]">
                <div className="flex items-center gap-1.5 mb-2">
                  <Monitor className="w-3 h-3 text-emerald-400" />
                  <span className="text-[9px] uppercase tracking-[0.15em] text-white/40">
                    Company Info
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40">Reputation</span>
                  <span className="text-white font-medium">{reputation.toFixed(0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40">Products</span>
                  <span className="text-white font-medium">{totalProducts}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40">Team Size</span>
                  <span className="text-white font-medium">{employeeCount}</span>
                </div>
              </div>
            )}
          </WallDisplay>

          {/* Right display: Rotating highlight or onboarding prompts */}
          <WallDisplay>
            <div className="w-full">
              <div className="flex items-center gap-1.5 mb-3">
                <Star className="w-3 h-3 text-amber-400" />
                <span className="text-[9px] uppercase tracking-[0.15em] text-white/40">
                  {isEmptyState ? 'Next Steps' : 'Highlights'}
                </span>
              </div>
              {isEmptyState ? (
                <OnboardingSlide
                  key={slideIndex}
                  icon={onboardingSlides[slideIndex].icon}
                  prompt={onboardingSlides[slideIndex].prompt}
                  destination={onboardingSlides[slideIndex].destination}
                  color={onboardingSlides[slideIndex].color}
                />
              ) : (
                <DisplaySlide
                  key={slideIndex}
                  icon={highlightSlides[slideIndex].icon}
                  value={highlightSlides[slideIndex].value}
                  label={highlightSlides[slideIndex].label}
                  color={highlightSlides[slideIndex].color}
                />
              )}
              {/* Slide indicators */}
              <div className="flex justify-center gap-1.5 mt-2">
                {Array.from({ length: slideCount }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-1 h-1 rounded-full transition-colors duration-300 ${
                      i === slideIndex ? 'bg-white/60' : 'bg-white/15'
                    }`}
                  />
                ))}
              </div>
            </div>
          </WallDisplay>
        </div>
      </div>

      {/* ── Reception Desk ───────────────────────────────────── */}
      <div className="relative">
        {/* Desk surface */}
        <div className="h-10 md:h-12 bg-theme-bg-primary/80 border-y border-theme-border
                        flex items-center justify-center shadow-sm">
          <div className="flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-theme-text-muted" />
            <span className="text-[10px] font-semibold tracking-[0.35em] uppercase text-theme-text-muted">
              Reception
            </span>
          </div>
        </div>
        {/* Desk front panel */}
        <div className="h-3 bg-theme-bg-primary border-b border-theme-border" />
      </div>

      {/* ── Lobby Floor / Seating Area ───────────────────────── */}
      <div className="flex-[3] relative flex items-center justify-center px-8
                      bg-theme-bg-content">
        {/* Subtle floor tile pattern */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(45deg, currentColor 25%, transparent 25%),
              linear-gradient(-45deg, currentColor 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, currentColor 75%),
              linear-gradient(-45deg, transparent 75%, currentColor 75%)
            `,
            backgroundSize: '40px 40px',
            backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0',
          }}
        />

        {/* Seating group */}
        <div className="relative z-10 flex items-end gap-4 md:gap-6">
          <PottedPlant />

          <div className="flex gap-2">
            <Chair />
            <Chair />
          </div>

          {/* Coffee table with newspaper */}
          <NewspaperProp onClick={() => setIsNewspaperOpen(true)} hasNews={hasNews} />

          <div className="flex gap-2">
            <Chair flip />
            <Chair flip />
          </div>

          <PottedPlant />
        </div>
      </div>

      {/* Newspaper overlay */}
      <NewspaperOverlay
        isOpen={isNewspaperOpen}
        onClose={() => setIsNewspaperOpen(false)}
        companyName={companyName}
        gameDate={gameDate ?? { day: companyState.day ?? 1, month: companyState.month ?? 1, year: companyState.year ?? 2000 }}
        eventHistory={eventHistory ?? []}
      />
    </div>
  );
};

export default LobbyReception;
